import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { flatten, isEqual } from 'lodash-es'
import { useInnerForcesEnvelopeInvalidation } from '@editor/hooks'
import { useResultsStore } from '@editor/stores'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from '@ui/snackbar'
import { getMemberCheckSettings, postCalcPositionChecks } from '@queries'
import { saveSingleMemberCheckSettings } from '@mutations'
import { CheckSettingsFormValues, MultiEditCheckSettingsElement } from '../types'

interface Params<SharedSettings> {
  /** German plural label used in the snackbars, e.g. 'Heißbemessungs-Einstellungen'. */
  settingsLabel: string
  /**
   * Produces what to persist for one member from the staged shared settings.
   * Only the section's own fields must be taken from `shared` — everything else,
   * including per-member identity fields, has to come from `existing`.
   */
  mergeSettings: (existing: SettingsOnMember, shared: SharedSettings) => SettingsOnMember
}

interface Result<SharedSettings> {
  save: (data: CheckSettingsFormValues<SharedSettings>) => Promise<unknown>
  isLoading: boolean
}

/**
 * Persists staged shared check settings onto every selected member. There is no
 * batch endpoint, so members are saved one by one; a failing member does not
 * abort the rest and is reported as a count instead.
 */
const useSaveSharedCheckSettings = <SharedSettings = SettingsOnMember>({
  settingsLabel,
  mergeSettings,
}: Params<SharedSettings>): Result<SharedSettings> => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const invalidateInnerForcesEnvelopes = useInnerForcesEnvelopeInvalidation()

  const setSingleMemberCheckSetting = useResultsStore(state => state.setSingleMemberCheckSetting)
  const replaceStructuralChecksForElements = useResultsStore(
    state => state.replaceStructuralChecksForElements,
  )

  // Persist the recomputed checks on the backend in the background — the store
  // is already updated from the staged elements, so we never reload.
  const { mutateAsync: recomputeChecks } = useMutation({
    mutationKey: [projectId, 'recomputeChecksMultiEditCheckSettings'],
    mutationFn: async (recomputeGuids: string[]) =>
      postCalcPositionChecks.request(projectId, recomputeGuids),
    onSuccess: () => invalidateInnerForcesEnvelopes(projectId as string),
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Neuberechnen der Nachweise'), {
        variant: 'error',
      })
    },
  })

  const { mutateAsync: save, isLoading } = useMutation(
    async (data: CheckSettingsFormValues<SharedSettings>) => {
      const savedElements: MultiEditCheckSettingsElement[] = []
      const failedErrors: AxiosError[] = []

      if (data.sharedSettings) {
        const { sharedSettings } = data

        for (const element of data.elements) {
          const checkSettings = mergeSettings(element.checkSettings, sharedSettings)
          // A member some or all of whose supports were taken out of the edit
          // (or that the staged fields never touched) merges back to its
          // existing settings unchanged — skip it rather than firing a no-op PUT.
          if (isEqual(checkSettings, element.checkSettings)) continue

          try {
            await saveSingleMemberCheckSettings.request(projectId, checkSettings)
            savedElements.push({ ...element, checkSettings })
          } catch (error) {
            failedErrors.push(error as AxiosError)
          }
        }
      }

      return { savedElements, failedErrors }
    },
    {
      onSuccess: ({
        savedElements,
        failedErrors,
      }: {
        savedElements: MultiEditCheckSettingsElement[]
        failedErrors: AxiosError[]
      }) => {
        if (savedElements.length === 0 && failedErrors.length === 0) return

        // Update the store directly from the staged checks (same as the
        // cross-section multi-edit) so no data needs to be reloaded.
        savedElements.forEach(element => setSingleMemberCheckSetting(element.checkSettings))

        const newChecks = flatten(savedElements.map(element => element.checks))
        replaceStructuralChecksForElements(
          savedElements.map(element => element.guid),
          newChecks,
        )

        // Persist the recomputed checks on the backend for the saved subset.
        if (savedElements.length > 0) {
          recomputeChecks(savedElements.map(element => element.guid))
        }

        if (failedErrors.length > 0) {
          enqueueSnackbar(
            buildErrorMessage(
              failedErrors[0],
              `${settingsLabel} konnten für ${failedErrors.length} Element(e) nicht gespeichert werden`,
            ),
            { variant: 'error' },
          )
        } else {
          enqueueSnackbar(`${settingsLabel} erfolgreich gespeichert`, { variant: 'success' })
        }
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, `Fehler beim Speichern der ${settingsLabel}`), {
          variant: 'error',
        })
        queryClient.invalidateQueries(getMemberCheckSettings.getKey(projectId))
      },
    },
  )

  return { save, isLoading }
}

export default useSaveSharedCheckSettings
